export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface FormValidationRules {
  name: {
    minLength: number;
    maxLength: number;
  };
  whatsapp: {
    minLength: number;
  };
  cnpj: {
    length: number;
  };
}

// Regras de validação simplificadas
export const VALIDATION_RULES: FormValidationRules = {
  name: {
    minLength: 2,
    maxLength: 100
  },
  whatsapp: {
    minLength: 10
  },
  cnpj: {
    length: 14
  }
};

/**
 * Formata número de WhatsApp para padrão brasileiro
 */
// Valid Brazilian area codes (DDDs)
const VALID_BR_DDDS = new Set([
  11,12,13,14,15,16,17,18,19,
  21,22,24,27,28,
  31,32,33,34,35,37,38,
  41,42,43,44,45,46,47,48,49,
  51,53,54,55,
  61,62,63,64,65,66,67,68,69,
  71,73,74,75,77,79,
  81,82,83,84,85,86,87,88,89,
  91,92,93,94,95,96,97,98,99,
]);

/**
 * Normaliza dígitos do WhatsApp: remove código de país 55 se presente
 * e limita a 11 dígitos (DDD + número).
 */
function normalizeBrDigits(raw: string): string {
  let cleaned = raw.replace(/\D/g, '');
  if ((cleaned.length === 12 || cleaned.length === 13) && cleaned.startsWith('55')) {
    cleaned = cleaned.slice(2);
  }
  return cleaned.slice(0, 11);
}

export function formatWhatsApp(value: string): string {
  const cleaned = normalizeBrDigits(value);

  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 7) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
}

/**
 * Formata CNPJ para padrão brasileiro
 */
export function formatCNPJ(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  if (cleaned.length <= 12) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  }
  
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
}

/**
 * Valida campo de nome - apenas verifica se não está vazio
 */
export function validateName(name: string): ValidationResult {
  const errors: string[] = [];
  const trimmedName = name.trim();
  
  if (!trimmedName) {
    errors.push('Nome é obrigatório');
  } else if (trimmedName.length < VALIDATION_RULES.name.minLength) {
    errors.push('Nome muito curto');
  } else if (trimmedName.length > VALIDATION_RULES.name.maxLength) {
    errors.push('Nome muito longo (máximo 100 caracteres)');
  } else if (!/^[a-zA-ZÀ-ÿ\s.'-]+$/.test(trimmedName)) {
    errors.push('Nome contém caracteres inválidos');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Valida campo de WhatsApp - apenas verifica se não está vazio e tem dígitos suficientes
 */
export function validateWhatsApp(whatsapp: string): ValidationResult {
  const errors: string[] = [];
  const cleaned = whatsapp.replace(/\D/g, '');

  if (!whatsapp.trim()) {
    errors.push('WhatsApp é obrigatório');
    return { isValid: false, errors };
  }

  if (cleaned.length !== 10 && cleaned.length !== 11) {
    errors.push('WhatsApp deve ter 10 ou 11 dígitos (DDD + número)');
    return { isValid: false, errors };
  }

  const ddd = cleaned.slice(0, 2);
  if (!VALID_BR_DDDS.has(parseInt(ddd, 10))) {
    errors.push('DDD inválido');
    return { isValid: false, errors };
  }

  if (cleaned.length === 11 && cleaned[2] !== '9') {
    errors.push('Número de celular deve começar com 9 após o DDD');
    return { isValid: false, errors };
  }

  // Reject trivial repeated digits (e.g. 11111111111, 99999999999)
  const subscriber = cleaned.slice(2);
  if (/^(\d)\1+$/.test(subscriber)) {
    errors.push('WhatsApp inválido');
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [] };
}

/**
 * Valida campo de CNPJ - apenas verifica se não está vazio e tem 14 dígitos
 */
export function validateCNPJ(cnpj: string): ValidationResult {
  const errors: string[] = [];
  const cleaned = cnpj.replace(/\D/g, '');
  
  if (!cnpj.trim()) {
    errors.push('CNPJ é obrigatório');
  } else if (cleaned.length !== VALIDATION_RULES.cnpj.length) {
    errors.push('CNPJ deve ter 14 dígitos');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Valida todo o formulário
 */
export function validateLeadForm(formData: { name: string; whatsapp: string; cnpj?: string }): ValidationResult {
  const nameValidation = validateName(formData.name);
  const whatsappValidation = validateWhatsApp(formData.whatsapp);
  
  const allErrors = [
    ...nameValidation.errors,
    ...whatsappValidation.errors
  ];
  
  if (formData.cnpj) {
    const cnpjValidation = validateCNPJ(formData.cnpj);
    allErrors.push(...cnpjValidation.errors);
  }
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
}

/**
 * Sanitiza dados do formulário removendo caracteres perigosos
 */
export function sanitizeFormData(formData: { name: string; whatsapp: string; cnpj?: string }) {
  // Strip HTML tags and trim
  const stripHtml = (str: string) => str.replace(/<[^>]*>/g, '').trim();
  
  return {
    name: stripHtml(formData.name).slice(0, VALIDATION_RULES.name.maxLength),
    whatsapp: formatWhatsApp(formData.whatsapp),
    ...(formData.cnpj ? { cnpj: formatCNPJ(formData.cnpj) } : {})
  };
}